import { cn } from 'domain/core';
import React, { useMemo, useRef } from 'react';

import { Card } from 'components';
import { AlertPanelDefinition } from 'domain/models/api.models';
import colors from 'tailwindcss/colors';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dateFormat, dateTimeFormat } from 'domain/utils/date.utils';
import { Alert } from 'domain/models';
import {
	Bar,
	ComposedChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';
import { format } from 'date-fns';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const palette = [
	['bg-orange-700', colors.orange[700]],
	['bg-amber-400', colors.amber[400]],
	['bg-yellow-300', colors.yellow[300]],
	['bg-sky-300', colors.sky[300]],
];

export const PanelCard = ({ item }: { item: AlertPanelDefinition }) => {
	const { t } = useTranslation(['labels']);

	const isAlertInsideRange = (alert: Alert, start: number, end: number) => {
		const startRangeAlert = new Date(alert?.alertTime).getTime() ?? -1;
		const endRangeAlert = new Date(alert?.endTime).getTime() ?? -1;

		if (!endRangeAlert) {
			// Is in progress, so we need to check the start
			return start <= startRangeAlert;
		}
		// Check if overlaps
		return start <= startRangeAlert && end >= endRangeAlert;
	};
	// Ranges are: < 24h, 24-72h, 72h - 7d, > 7d
	const findInsideRange = (start: number, end: number) => {
		return (
			item?.alerts?.filter((alert) => {
				// It is inside the range if the difference is greater than the start and less than the end
				return isAlertInsideRange(alert, start, end);
			}) ?? []
		);
	};

	const currentDate = new Date().getTime();

	const chunks = [
		[currentDate - ONE_DAY_MS, currentDate + ONE_DAY_MS], // 0 - 24h
		[currentDate - 3 * ONE_DAY_MS, currentDate - ONE_DAY_MS], // 24h - 72h
		[currentDate - 7 * ONE_DAY_MS, currentDate - 3 * ONE_DAY_MS], // 72h - 7d
		[currentDate - 30 * ONE_DAY_MS, currentDate - 7 * ONE_DAY_MS], // 7d - 30d
	];

	const ranges = chunks.map((chunk) => {
		return findInsideRange(chunk[0], chunk[1]);
	});

	return (
		<Card
			className={cn(
				item.deleted ? 'border-0 opacity-50' : '',
				'min-h-[400px]'
			)}>
			<Card.Header>
				<Card.Badge status={'OK'}></Card.Badge>
				<Card.Titles>
					<Card.Title>{item?.name}</Card.Title>
					<Card.Subtitle className='space-between flex'>
						<div>
							{t('labels:lastAlert')}: {dateTimeFormat(item?.lastAlert)}
						</div>
					</Card.Subtitle>
				</Card.Titles>
				<Card.Info>
					<Card.InfoItemLabel>
						<div className='text-base'>{item?.station?.label}</div>
					</Card.InfoItemLabel>
					<Card.InfoItemLabel>
						<div></div>
					</Card.InfoItemLabel>
				</Card.Info>
			</Card.Header>
			<Card.Body>
				<React.Suspense fallback={<></>}>
					<RangeSummary ranges={ranges} />
					<RangeCharts ranges={item?.alerts} />
				</React.Suspense>
			</Card.Body>
		</Card>
	);
};

const RangeCharts = ({ ranges }: { ranges: Alert[] }) => {
	// Create a chart where X is the days (from -30 to 0) and Y is the hour of the day, in
	// order to show the distribution of the alerts in the last 30 days
	const now = Date.now();

	const days = Array.from({ length: 30 }, (_, i) => -i);

	/** Check if the alert range [] */
	const alertInsideRange = (alert: Alert, start: number, end: number) => {
		const startRangeAlert = new Date(alert?.alertTime).getTime() ?? -1;
		const endRangeAlert = new Date(alert?.endTime).getTime() ?? -1;

		// Check if overlaps

		return !(endRangeAlert < start || startRangeAlert > end);
	};

	const getAlertsInRange = (from: number, to: number) => {
		return ranges.filter((alert) => {
			return alertInsideRange(alert, from, to);
		});
	};

	const getColorRange = (time: number) => {
		// If within the last 24 hours, return the first color
		if (time > now - 24 * 60 * 60 * 1000) {
			return palette[0][1];
		}

		// If within the last 72 hours, return the second color
		if (time > now - 72 * 60 * 60 * 1000) {
			return palette[1][1];
		}

		// If within the last 7 days, return the third color
		if (time > now - 7 * 24 * 60 * 60 * 1000) {
			return palette[2][1];
		}

		// Otherwise, return the last color
		return palette[3][1];
	};

	const monthData = days
		.map((dayPadding) => {
			const slotStart = new Date(
				now + dayPadding * 24 * 60 * 60 * 1000
			).setHours(0, 0, 0, 0);
			const slotEnd = new Date(now + dayPadding * 24 * 60 * 60 * 1000).setHours(
				23,
				59,
				59,
				999
			);

			const alerts = getAlertsInRange(slotStart, slotEnd);

			// Now, we can create the data for the day
			const dayData = alerts.map((alert) => {
				// We can have alerts during different days, so we need to check the start and end with slotStart and slotEnd
				const alertTime = new Date(alert.alertTime).getTime();
				const alertEnd = new Date(alert.endTime).getTime();
				const start = Math.max(alertTime, slotStart);
				const end = Math.min(alertEnd, slotEnd);

				const startHour = new Date(start).getHours();
				const endHour = new Date(end).getHours();

				return {
					x: new Date(slotStart).getTime(),
					alert,
					fill: getColorRange(start),
					y: [startHour, endHour],
				};
			});
			//.sort((a, b) => a.duration - b.duration);

			if (dayData.length === 0) {
				return [
					{
						// We need at least one for the chart to show the day
						x: new Date(slotStart).getTime(),
						fill: getColorRange(slotStart),
						y: [0, 0],
					},
				];
			}
			return dayData;
		})
		.flat()
		.reverse();

	const currentAlert = useRef<Alert | null>();

	return (
		<ResponsiveContainer width='100%' height={200}>
			<ComposedChart
				data={monthData}
				margin={{
					top: 5,
					right: 30,
					left: -10,
					bottom: 5,
				}}>
				<Tooltip
					content={<PanelTooltip currentAlert={currentAlert} />}
					shared={false}
				/>
				<Bar
					cursor='pointer'
					dataKey='y'
					barSize={10}
					onMouseEnter={(data) => {
						// Highlight the alert
						currentAlert.current = data.payload.alert;
					}}
					onMouseLeave={() => {
						currentAlert.current = null;
					}}
				/>
				<YAxis
					type='number'
					scale='time'
					domain={[0, 24]}
					ticks={[0, 12, 24]}
					tick={<HourFormatter />}
					tickFormatter={(value) => {
						return `${value}h`;
					}}
				/>
				<XAxis
					dataKey='x'
					domain={[
						new Date().getTime() - 30 * 24 * 60 * 60 * 1000,
						new Date().getTime(),
					]}
					type='number'
					scale='time'
					ticks={[
						new Date().getTime() - 30 * 24 * 60 * 60 * 1000,
						new Date().getTime(),
					]}
					tick={<DateFormatter />}
					tickFormatter={(value) => {
						return format(value, 'dd/MM');
					}}
				/>
			</ComposedChart>
		</ResponsiveContainer>
	);
};

const HourFormatter = (props: any) => {
	const { x, y, payload, tickFormatter } = props;
	return (
		<g transform={`translate(${x},${y})`}>
			<text x={0} y={0} textAnchor='end' fill='#666' fontSize={10}>
				{tickFormatter(payload.value)}
			</text>
		</g>
	);
};

const PanelTooltip = ({ active, payload, currentAlert }: any) => {
	if (!active || !payload || payload.length === 0 || !currentAlert.current) {
		return null;
	}

	const currentX = new Date(payload[0].payload.date).getTime();

	return (
		<div className='max-w-[200px] rounded-md bg-white p-2 shadow-md'>
			<span className='text-sm'>{dateFormat(currentX)}</span>
			<div className='text-xs font-bold text-primary-500'>
				{dateTimeFormat(currentAlert?.current?.alertTime)} -{' '}
				{dateTimeFormat(currentAlert?.current?.endTime)}
			</div>
		</div>
	);
};

const DateFormatter = (props: any) => {
	const { x, y, payload, tickFormatter } = props;
	return (
		<g transform={`translate(${x},${y})`}>
			<text
				x={0}
				y={0}
				dy={10}
				textAnchor='end'
				fill='#666'
				transform='rotate(-35)'
				fontSize={10}>
				{tickFormatter(payload.value)}
			</text>
		</g>
	);
};

const RangeSummary = ({ ranges }: { ranges: Alert[][] }) => {
	const hoursRanges = ['24h', '72h', '7d', '7-30d'];

	const { t } = useTranslation(['labels']);

	/**
	 * The blocks are rows with the following information:
	 * - Color: pick a color from the palette
	 * - Label: the label to show (< 24, < 72, < 7, > 7) and the number of alerts on the other side
	 */

	const blocks = useMemo(() => {
		return ranges.map((range, index) => {
			const label = t(`labels:range.${hoursRanges[index]}`);
			return {
				color: palette[index],
				label,
				range: range.length,
			};
		});
	}, [ranges]);

	return (
		<div className='flex flex-col gap-0 pt-2 text-sm'>
			{blocks.map((block, index) => (
				<div
					key={`block-${index}`}
					className={cn(
						'space-between m-0 flex w-full justify-between gap-0 px-2 py-[2px]',
						block.color
					)}>
					<div>{block.label}</div>
					<div>{block.range}</div>
				</div>
			))}
		</div>
	);
};
